import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, FormHelperText, Grid, SvgIcon, Theme, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { FC, useEffect, useState } from 'react';
import { PaymentMethodStatusData } from '../features/multipleMethodsHome/MultipleMethodsHome';
import { PayFac } from '../gql-types.generated';
import { ApteanPaySDK, ApteanPaySDKBankComponent, ApteanPaySDKCardComponent, useApteanPay } from '../util/ApteanPay';
import { isNmiPayFac } from '../util/PayFac';
import LoadingMask from './LoadingMask';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paymentMethodAction: {
      width: '100%',
    },
    padding: {
      padding: theme.spacing(2, 2, 0, 2),
    },
    buttonPadding: {
      padding: theme.spacing(1),
    },
    paymentMethodForm: {
      padding: theme.spacing(2),
    },
    mainHeaderText: {
      fontWeight: 500,
    },
    upperHeaderText: {
      fontSize: 10,
      letterSpacing: 1.5,
      fontWeight: 500,
    },
    arrowBack: {
      cursor: 'pointer',
    },
    textField: {
      backgroundColor: theme.palette.grey[100],
    },
    bankIframe: {
      width: '100%',
      padding: theme.spacing(1, 0, 0, 2),
      minHeight: 208,
    },
  }),
);
interface PaymentMethodEditProps {
  handleBack?: () => void;
  onMobile: boolean;
  statusData: PaymentMethodStatusData;
  includeHolder?: boolean;
  submit?: (bankInfo: { holderName?: string; accountNumber: string; routingNumber: string; accountType: string }) => void;
  paymentMethodExists: boolean;
  fieldsErrorMessage: string | undefined;
  setFieldsErrorMessage: (fieldsErrorMessage: string | undefined) => void;
  payfac?: PayFac;
  onTokenizeInjectSuccess?: (apteanPay: ApteanPaySDK, bankComponent: ApteanPaySDKBankComponent) => void;
  merchantTenantId?: string;
  customerId?: string;
  setSendingPayment?: (sendingPayment: boolean) => void;
  onTokenize?: (apteanPay: ApteanPaySDK, cardComponent: ApteanPaySDKCardComponent) => void;
}
/*
  This component allows the input of US bank account information
    - onBankInfoChange: Configure a function to handle changes to the information in the fields.
        This allows the owner of this component the ability to handle changes at their own pace.

  Error state is handled by the status data obtained by the owning component
*/
const PaymentMethodEdit: FC<PaymentMethodEditProps> = props => {
  const classes = useStyles();
  const {
    handleBack,
    onMobile,
    statusData,
    submit,
    paymentMethodExists,
    fieldsErrorMessage,
    payfac,
    onTokenizeInjectSuccess,
    merchantTenantId,
    customerId,
    onTokenize,
    setSendingPayment,
  } = props;

  const [methodValid, setMethodValid] = useState(false);
  const [tokenErrorMessage, setTokenErrorMessage] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [showLoading, setShowLoading] = useState(false);

  const { apteanPay, sdkComponent, configure, mountComponent } = useApteanPay();

  useEffect(() => {
    if (merchantTenantId && isNmiPayFac(payfac)) {
      configure(merchantTenantId, customerId, false, 'bank');
    }
  }, [merchantTenantId, customerId, payfac]);

  useEffect(() => {
    if (apteanPay && sdkComponent) {
      setShowLoading(true);
      mountComponent('js_sdk_bank', '#js_sdk_button', () => {
        setShowLoading(false);
        setMethodValid(true);
      });
      if (onTokenizeInjectSuccess) {
        onTokenizeInjectSuccess(apteanPay, sdkComponent);
      }
    }
  }, [sdkComponent]);

  const onbankSubmit = () => {
    if (onTokenize && apteanPay && sdkComponent) {
      if (setSendingPayment) setSendingPayment(true);
      onTokenize(apteanPay, sdkComponent);
    }
  };
  useEffect(() => {
    if (statusData?.tokenError?.message) {
      setTokenErrorMessage(statusData?.tokenError?.message);
    } else {
      setTokenErrorMessage(undefined);
    }

    if (statusData?.error?.message) {
      let message = statusData?.error?.message.trim();
      if (message && message.toLowerCase() === 'invalid parameter(s).') {
        let errorCount = 0;
        message = 'Invalid';

        if (statusData?.invalidRoutingNumber) {
          message += ' Routing Number';
          errorCount += 1;
        }
        if (statusData?.invalidAccountNumber) {
          message += errorCount > 0 ? ' and ' : ' ';
          message += 'Account Number';
        }
        message += '.';
      }
      setErrorMessage(message);
    } else {
      setErrorMessage(undefined);
    }
  }, [statusData?.tokenError?.message, statusData?.error?.message]);
  return (
    <Box>
      <LoadingMask loading={showLoading} />
      <Grid container className={classes.padding}>
        {onMobile && handleBack && (
          <Grid item xs={2}>
            <SvgIcon
              color={'primary'}
              fontSize={'large'}
              className={classes.arrowBack}
              onClick={() => {
                handleBack();
              }}
              data-cy="back"
            >
              <ArrowBackIcon />
            </SvgIcon>
          </Grid>
        )}
        <Grid item xs={onMobile ? 10 : 12}>
          <Typography variant="title" className={classes.mainHeaderText} id="add-bank-account">
            {paymentMethodExists ? 'Edit Payment Method' : 'Add Bank Account'}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2} className={classes.paymentMethodForm}>
        {isNmiPayFac(payfac) && <Box id="js_sdk_bank" className={classes.bankIframe}></Box>}
        <Grid container className={classes.buttonPadding} justifyContent={onMobile ? 'flex-end' : 'space-between'} spacing={1}>
          {handleBack && !onMobile && (
            <Grid item>
              <Button
                className={classes.paymentMethodAction}
                color="primary"
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  handleBack();
                }}
                data-cy="back"
              >
                Back
              </Button>
            </Grid>
          )}
          {submit && (
            <Grid item>
              <Button
                className={classes.paymentMethodAction}
                variant="contained"
                color="primary"
                onClick={onbankSubmit}
                disabled={!methodValid}
                aria-describedby={`${tokenErrorMessage ? 'error-text-one' : undefined} ${
                  errorMessage ? 'error-text-two' : undefined
                } ${fieldsErrorMessage ? 'error-text-three' : undefined}`}
              >
                Save
              </Button>
            </Grid>
          )}
        </Grid>
        {tokenErrorMessage && (
          <FormHelperText error id="error-text-one">
            {tokenErrorMessage}
          </FormHelperText>
        )}
        {errorMessage && (
          <FormHelperText error id="error-text-two">
            {errorMessage}
          </FormHelperText>
        )}
        {fieldsErrorMessage && (
          <FormHelperText error id="error-text-three">
            {fieldsErrorMessage}
          </FormHelperText>
        )}
      </Grid>
    </Box>
  );
};

export default PaymentMethodEdit;
